/** 필터 필드 id — 그대로 폼 값 키가 된다. */
export const FIELD_NAMES = {
  shipperCode: 'shipperCode',
  orderNumbers: 'orderNumbers',
  styleNumber: 'styleNumber',
  goodsCode: 'goodsCode',
} as const

/**
 * 배송유형(TRS_TYPE) 셀렉트 옵션 — BE `TransportType` enum 의 한글 표시 문자열 전수.
 * 저장 API 는 @NotBlank 만 검증하고 미지값은 조회 시 PARCEL 로 fallback 되므로(BE 실측),
 * FE 셀렉트가 유효값 입력을 보장해야 한다.
 */
export const TRS_TYPE_OPTIONS = ['새벽배송', '당일배송', '익일배송', '택배', '택배-명품', '택배-단포', '용차', '택배-현지', '특송'] as const

/**
 * 운송사(B2B_CARRIER) 셀렉트 옵션 — BE 유효 입력 집합은 {'중앙일보', ''} 뿐이다.
 * (쓰기 경로: b2bCarrier='중앙일보' + 화주/유형/센터 조건 충족 시에만 CARRIER='JA', 그 외 '' 저장)
 */
export const B2B_CARRIER_OPTIONS = ['중앙일보', ''] as const

/** 이동지시번호 다건 입력 상한 — 1.0/공통 MultipleInputV2 와 동일. 초과분은 잘라낸다. */
export const ORDER_NUMBERS_MAX_ENTRIES = 500

/** 그리드 페이지 크기 — 핸드오프 §테이블 동작 계약 "pageSize 10000, 페이지네이션 없이 전체 로드". */
export const PAGE_SIZE = 10000

/** HEAD 서버 엑셀 상세사유 최소 길이 — BE MIN_DETAILED_REASON_LENGTH 와 동일. */
export const EXCEL_REASON_MIN_LENGTH = 5

/** HEAD 서버 엑셀 행수 상한 — BE MAX_EXCEL_ROW_SIZE 와 동일(초과 400). 선차단은 왕복 절약용. */
export const EXCEL_ROW_LIMIT = 100_000

/** 삭제 문서번호 최대 건수 — BE DeleteStockTransferDocumentsUsecase 제약과 동일. */
export const DELETE_MAX_DOCUMENTS = 100

/** 삭제 사유 byte 범위 — BE legacy byte 검증(ASCII 1B·<U+0800 2B·그 외 3B)과 동일. 한글 기준 5~30자. */
export const DELETE_REASON_MIN_BYTES = 15
export const DELETE_REASON_MAX_BYTES = 90

/**
 * BE 삭제 사유 검증과 동일한 legacy byte 길이 — ASCII 1B, U+0800 미만 2B, 그 외 3B.
 * (BE 는 첫 NUL 에서 자르지만 입력 UI 에서 NUL 은 들어올 수 없어 생략)
 */
export const legacyByteLength = (text: string): number =>
  [...text].reduce((sum, ch) => {
    const code = ch.codePointAt(0) ?? 0
    if (code < 0x80) return sum + 1
    if (code < 0x800) return sum + 2
    return sum + 3
  }, 0)

/** 표 id(생성본 columnLayout 키)와 그리드 코드(docs/conventions/grid-code.md) — 컬럼 배치 저장·화면관리 그리드 설정이 같은 값을 쓴다(LOGISPRD-17251). */
export const HEAD_TABLE_ID = 'est01HeadTable'
export const ITEM_TABLE_ID = 'est01ItemTable'
export const HEAD_GRID_CD = 'EST04_HEAD'
export const ITEM_GRID_CD = 'EST04_ITEM'

export const MESSAGES = {
  searchFailed: '조회 중 오류가 발생했습니다.',
  shipperRequired: '화주명을 선택해 주세요.',
  noSelection: '선택된 데이터가 없습니다.',
  noDirtyRows: '변경된 내용이 없습니다.',
  /** [저장] 필수 칸 빈 값 — 1.0 CommonUtil.onSaveData 알럿과 같은 뜻. 편집 행만 검사해 화면 행 번호 대신 문서번호·순번으로 짚는다. */
  requiredMissing: (documentNumber: string, title: string, sequence?: string | null) =>
    `이동지시문서 ${documentNumber}${sequence ? ` 순번 ${sequence}` : ''}의 필수값 '${title}' 열이 입력되지 않았습니다. 확인 후 다시 시도해 주세요.`,
  /** 저장은 체크한 행만 처리한다 — 편집은 했지만 그 행을 체크하지 않은 경우를 구분해 안내한다. */
  noDirtyRowsInSelection: '선택한 행에 변경된 내용이 없습니다. 저장할 행을 체크해 주세요.',
  mixedShippers: '화주가 서로 다른 문서는 함께 처리할 수 없습니다. 동일 화주의 문서만 선택해 주세요.',
  saveDone: '저장되었습니다.',
  saveFailed: '저장 중 오류가 발생했습니다.',
  confirmFailed: '확정 중 오류가 발생했습니다.',
  deleteFailed: '삭제 중 오류가 발생했습니다.',
  deleteMaxExceeded: `삭제는 한 번에 최대 ${DELETE_MAX_DOCUMENTS}건까지 가능합니다.`,
  excelNoRows: '다운로드할 데이터가 없습니다.',
  excelReasonRequired: '사유코드를 선택해 주세요.',
  excelReasonDetailTooShort: `상세사유를 ${EXCEL_REASON_MIN_LENGTH}글자 이상 입력해 주세요.`,
  excelRowLimitExceeded: `엑셀 다운로드 최대 행수(${EXCEL_ROW_LIMIT.toLocaleString()}행)를 초과했습니다. 조회 조건을 확인해 주세요.`,
  confirmDone: '확정되었습니다.',
  deleteNoAuth: '삭제 권한이 없습니다.',
  /** 엑셀 업로드 — 데모에 새로 생긴 기능이라 문구도 여기서 모은다. */
  uploadNoFile: '업로드할 파일을 선택해 주세요.',
  uploadEmpty: '업로드할 행이 없습니다.',
  uploadNoShipper: '화주를 선택한 뒤 업로드해 주세요. 업로드는 선택한 화주로 저장됩니다.',
  uploadMissingKey: (line: number) => `${line}번째 줄에 이동지시문서·순번이 없습니다. 내려받은 양식 그대로 올려 주세요.`,
  /** 신규 이동(문서번호 없는 행) — 1.0 업로드 양식의 필수값. */
  uploadCreateInvalid: (line: number) =>
    `${line}번째 줄의 신규 이동 필수값(이동예정일자·바코드(Source)·센터명(Source)·센터명(Destination)·지시수량)이 없거나 올바르지 않습니다.`,
  uploadCreateMixedDate: '신규 이동은 한 번에 이동예정일자 하나만 올릴 수 있습니다. 날짜별로 나눠 올려 주세요.',
  /** 상품코드 서치헬퍼 — goods-codes 는 무페이징 distinct 라 조건 없이 받지 않는다(index.tsx enabled). */
  goodsHelperConditionRequired: '브랜드명 또는 상품명을 입력한 뒤 조회해 주세요.',
} as const

/** 값이 빈 셀렉트 옵션의 표시 문구 — 표 편집 셀에서 '고르지 않음'을 보이는 자리. */
export const EMPTY_OPTION_LABEL = '(없음)'
